import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { Link } from "@tanstack/react-router";
import { submitQuizResponse, type QuizAnswers } from "@/lib/careers.functions";
import { getOrCreateSessionId } from "@/lib/academy-session";
import { PrimaryButton } from "@/components/brand/PrimaryButton";
import { SecondaryButton } from "@/components/brand/SecondaryButton";
import { cn } from "@/lib/utils";

type Path = "strong-fit" | "possible-fit" | "not-a-fit";
type Partial = {
  licensed?: QuizAnswers["licensed"];
  weekly_hours?: QuizAnswers["weekly_hours"];
  motivation: QuizAnswers["motivation"];
  relationships?: QuizAnswers["relationships"];
  legacy_meaning: string;
};

const STEPS = 5;

const Q1: { v: QuizAnswers["licensed"]; label: string }[] = [
  { v: "life-only", label: "Yes — Life only" },
  { v: "life-health", label: "Yes — Life + Health" },
  { v: "in-process", label: "In process" },
  { v: "not-yet", label: "Not yet" },
];
const Q2: { v: QuizAnswers["weekly_hours"]; label: string }[] = [
  { v: "5-10", label: "5–10 hrs" },
  { v: "10-20", label: "10–20 hrs" },
  { v: "20-40", label: "20–40 hrs" },
  { v: "full-time", label: "Full time" },
];
const Q3: { v: QuizAnswers["motivation"][number]; label: string }[] = [
  { v: "income", label: "Income potential" },
  { v: "helping-families", label: "Helping families" },
  { v: "flexibility", label: "Flexible schedule" },
  { v: "career-change", label: "Career change" },
  { v: "building-team", label: "Building a team" },
];
const Q4: { v: QuizAnswers["relationships"]; label: string }[] = [
  { v: "whole-point", label: "That's the whole point" },
  { v: "important", label: "Important but not central" },
  { v: "fast-transactions", label: "I prefer fast transactions" },
  { v: "unsure", label: "Unsure" },
];

export function AdvisorFitQuiz() {
  const submit = useServerFn(submitQuizResponse);
  const [step, setStep] = useState(0);
  const [a, setA] = useState<Partial>({ motivation: [], legacy_meaning: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ score: number; path: Path } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const stepValid = (() => {
    switch (step) {
      case 0: return !!a.licensed;
      case 1: return !!a.weekly_hours;
      case 2: return a.motivation.length >= 1;
      case 3: return !!a.relationships;
      case 4: return a.legacy_meaning.trim().length >= 10;
    }
    return false;
  })();

  async function handleSubmit() {
    setBusy(true);
    setError(null);
    try {
      const session_id = getOrCreateSessionId();
      const res = await submit({
        data: {
          session_id,
          answers: {
            licensed: a.licensed!,
            weekly_hours: a.weekly_hours!,
            motivation: a.motivation,
            relationships: a.relationships!,
            legacy_meaning: a.legacy_meaning.trim(),
          },
        },
      });
      setResult({ score: res.fit_score, path: res.recommended_path });
    } catch (e) {
      setError(e instanceof Error ? e.message : "Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  if (result) return <ResultCard score={result.score} path={result.path} />;

  return (
    <div
      className="mx-auto w-full max-w-[720px] bg-white p-8 md:p-10 border-t-[3px] border-bronze-600 shadow-[0_20px_60px_-30px_rgba(168,139,92,0.25)]"
      style={{ borderRadius: "var(--radius-card, 4px)" }}
    >
      {/* Progress dots */}
      <div className="flex items-center justify-center gap-2 mb-8">
        {Array.from({ length: STEPS }).map((_, i) => (
          <span
            key={i}
            className={cn(
              "h-1.5 rounded-full transition-all duration-300",
              i === step ? "w-8 bg-bronze-600" : i < step ? "w-4 bg-bronze-400" : "w-4 bg-charcoal-500/20",
            )}
          />
        ))}
      </div>

      <div key={step} className="animate-in fade-in slide-in-from-bottom-2 duration-300">
        {step === 0 && (
          <Question title="Are you currently licensed?">
            <SingleSelect
              options={Q1}
              value={a.licensed}
              onChange={(v) => setA({ ...a, licensed: v })}
            />
          </Question>
        )}
        {step === 1 && (
          <Question title="How much time can you commit weekly?">
            <SingleSelect
              options={Q2}
              value={a.weekly_hours}
              onChange={(v) => setA({ ...a, weekly_hours: v })}
            />
          </Question>
        )}
        {step === 2 && (
          <Question
            title="What's drawing you to insurance?"
            hint="Choose up to two."
          >
            <MultiSelect
              options={Q3}
              max={2}
              value={a.motivation}
              onChange={(v) => setA({ ...a, motivation: v })}
            />
          </Question>
        )}
        {step === 3 && (
          <Question title="How do you feel about long-term client relationships?">
            <SingleSelect
              options={Q4}
              value={a.relationships}
              onChange={(v) => setA({ ...a, relationships: v })}
            />
          </Question>
        )}
        {step === 4 && (
          <Question
            title="In your own words, what does &lsquo;legacy&rsquo; mean to you?"
            hint="One to three sentences."
          >
            <textarea
              value={a.legacy_meaning}
              onChange={(e) => setA({ ...a, legacy_meaning: e.target.value.slice(0, 800) })}
              rows={5}
              placeholder="Write honestly. There's no right answer."
              className="w-full bg-cream-50/40 border border-charcoal-500/15 p-4 font-sans text-[15px] leading-relaxed text-charcoal-900 placeholder:text-charcoal-500/60 focus:outline-none focus:border-bronze-600 focus:ring-2 focus:ring-bronze-400/30"
              style={{ borderRadius: "var(--radius-input, 3px)" }}
            />
            <div className="mt-2 text-right text-[11px] uppercase tracking-[0.18em] text-charcoal-500">
              {a.legacy_meaning.trim().length} characters
            </div>
          </Question>
        )}
      </div>

      {error && (
        <p className="mt-4 text-sm text-[color:var(--color-danger,#B43A3A)]">{error}</p>
      )}

      <div className="mt-10 flex items-center justify-between gap-3 pt-6 border-t border-charcoal-500/10">
        <button
          type="button"
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          disabled={step === 0}
          className="font-sans text-[12px] uppercase tracking-[0.18em] text-charcoal-500 hover:text-navy-800 disabled:opacity-30 disabled:pointer-events-none"
        >
          ← Back
        </button>
        <span className="font-sans text-[11px] uppercase tracking-[0.22em] text-charcoal-500">
          {step + 1} / {STEPS}
        </span>
        {step < STEPS - 1 ? (
          <PrimaryButton
            type="button"
            disabled={!stepValid}
            onClick={() => setStep((s) => Math.min(STEPS - 1, s + 1))}
          >
            Continue
          </PrimaryButton>
        ) : (
          <PrimaryButton type="button" disabled={!stepValid || busy} onClick={handleSubmit}>
            {busy ? "Scoring…" : "See My Result"}
          </PrimaryButton>
        )}
      </div>
    </div>
  );
}

function Question({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <h3
        className="font-display text-[26px] md:text-[30px] font-semibold text-navy-900 leading-snug"
        dangerouslySetInnerHTML={{ __html: title }}
      />
      {hint && (
        <p className="mt-2 font-sans text-[13px] text-charcoal-500">{hint}</p>
      )}
      <div className="mt-6">{children}</div>
    </div>
  );
}

function SingleSelect<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { v: T; label: string }[];
  value: T | undefined;
  onChange: (v: T) => void;
}) {
  return (
    <div className="grid gap-2.5">
      {options.map((o) => {
        const active = value === o.v;
        return (
          <button
            key={o.v}
            type="button"
            onClick={() => onChange(o.v)}
            className={cn(
              "group text-left px-5 py-4 border transition-colors font-sans text-[15px]",
              active
                ? "border-bronze-600 bg-bronze-600/8 text-navy-900"
                : "border-charcoal-500/15 text-charcoal-900 hover:border-bronze-600/60 hover:bg-cream-50/60",
            )}
            style={{ borderRadius: "var(--radius-input, 3px)" }}
          >
            <span className="flex items-center justify-between gap-3">
              <span>{o.label}</span>
              <span
                aria-hidden
                className={cn(
                  "h-3 w-3 rounded-full border transition-colors",
                  active ? "border-bronze-600 bg-bronze-600" : "border-charcoal-500/30",
                )}
              />
            </span>
          </button>
        );
      })}
    </div>
  );
}

function MultiSelect<T extends string>({
  options,
  value,
  onChange,
  max,
}: {
  options: { v: T; label: string }[];
  value: T[];
  onChange: (v: T[]) => void;
  max: number;
}) {
  return (
    <div className="grid gap-2.5">
      {options.map((o) => {
        const active = value.includes(o.v);
        const disabled = !active && value.length >= max;
        return (
          <button
            key={o.v}
            type="button"
            disabled={disabled}
            onClick={() => {
              if (active) onChange(value.filter((x) => x !== o.v));
              else onChange([...value, o.v]);
            }}
            className={cn(
              "text-left px-5 py-4 border transition-colors font-sans text-[15px]",
              active
                ? "border-bronze-600 bg-bronze-600/8 text-navy-900"
                : disabled
                  ? "border-charcoal-500/10 text-charcoal-500/50 cursor-not-allowed"
                  : "border-charcoal-500/15 text-charcoal-900 hover:border-bronze-600/60 hover:bg-cream-50/60",
            )}
            style={{ borderRadius: "var(--radius-input, 3px)" }}
          >
            <span className="flex items-center justify-between gap-3">
              <span>{o.label}</span>
              <span
                aria-hidden
                className={cn(
                  "h-4 w-4 border transition-colors flex items-center justify-center text-[10px]",
                  active
                    ? "border-bronze-600 bg-bronze-600 text-cream-50"
                    : "border-charcoal-500/30",
                )}
              >
                {active ? "✓" : ""}
              </span>
            </span>
          </button>
        );
      })}
      <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-charcoal-500">
        {value.length} of {max} selected
      </p>
    </div>
  );
}

function ResultCard({ score, path }: { score: number; path: Path }) {
  if (path === "strong-fit") {
    return (
      <div
        className="mx-auto w-full max-w-[720px] bg-white p-10 border-t-[3px] border-[color:var(--color-success,#0E7C5A)] shadow-[0_20px_60px_-30px_rgba(14,124,90,0.3)] text-center"
        style={{ borderRadius: "var(--radius-card, 4px)" }}
      >
        <p className="font-sans text-[11px] uppercase tracking-[0.22em] text-[color:var(--color-success,#0E7C5A)]">
          Fit Score · {score}
        </p>
        <h3 className="mt-4 font-display text-[40px] font-semibold text-navy-900 leading-tight">
          Strong fit.
        </h3>
        <p className="mt-4 max-w-[480px] mx-auto font-sans text-[16px] leading-relaxed text-charcoal-900">
          You think the way we think. Time and care invested in families compound
          into the kind of practice we&rsquo;re building together. Let&rsquo;s talk.
        </p>
        <div className="mt-8">
          <PrimaryButton asChild size="lg">
            <Link to="/careers/apply">Continue to Application →</Link>
          </PrimaryButton>
        </div>
      </div>
    );
  }
  if (path === "possible-fit") {
    return (
      <div
        className="mx-auto w-full max-w-[720px] bg-white p-10 border-t-[3px] border-bronze-600 text-center"
        style={{ borderRadius: "var(--radius-card, 4px)" }}
      >
        <p className="font-sans text-[11px] uppercase tracking-[0.22em] text-bronze-600">
          Fit Score · {score}
        </p>
        <h3 className="mt-4 font-display text-[40px] font-semibold text-navy-900 leading-tight">
          There may be a path here.
        </h3>
        <p className="mt-4 max-w-[520px] mx-auto font-sans text-[16px] leading-relaxed text-charcoal-900">
          Some of what you shared aligns with how we work, and some pieces are still
          forming. The best next step isn&rsquo;t an application — it&rsquo;s a fifteen-minute
          conversation so we can both tell honestly.
        </p>
        <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
          <PrimaryButton asChild size="lg">
            <Link to="/book">Book a 15-min chat →</Link>
          </PrimaryButton>
          <Link
            to="/careers/apply"
            className="font-sans text-[12px] uppercase tracking-[0.18em] text-charcoal-500 hover:text-navy-800"
          >
            Or apply anyway
          </Link>
        </div>
      </div>
    );
  }
  return (
    <div
      className="mx-auto w-full max-w-[720px] bg-cream-50/60 p-10 border border-charcoal-500/15 text-center"
      style={{ borderRadius: "var(--radius-card, 4px)" }}
    >
      <p className="font-sans text-[11px] uppercase tracking-[0.22em] text-charcoal-500">
        Fit Score · {score}
      </p>
      <h3 className="mt-4 font-display text-[32px] font-semibold text-navy-900 leading-tight">
        Honest answer: probably not the right match.
      </h3>
      <p className="mt-4 max-w-[520px] mx-auto font-sans text-[15px] leading-relaxed text-charcoal-900">
        Currency Thoughts is built for strategists who treat client relationships as
        their craft. The shape of what you&rsquo;re looking for and the shape of what
        we&rsquo;ve built don&rsquo;t line up cleanly right now — and pretending otherwise
        wouldn&rsquo;t serve either of us.
      </p>
      <p className="mt-4 font-sans text-[14px] text-charcoal-500">
        Thank you for being honest. We mean that.
      </p>
    </div>
  );
}

export default AdvisorFitQuiz;